import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Copy } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { buildHourList } from '../../utils/slotMath';
import { writeTextToClipboard } from '../../utils/meetingExport';
import { showToast } from '../shared/Toast';
import { CalRow } from './CalRow';

function V2Notice({ children, action }) {
  return (
    <div
      style={{
        padding: '10px 12px',
        margin: '4px 0',
        background: 'var(--wv-card)',
        border: '1px dashed var(--wv-card-border-strong)',
        borderRadius: 8,
        color: 'var(--wv-text-muted)',
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span style={{ flex: 1 }}>{children}</span>
      {action}
    </div>
  );
}

function AddCalendarButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'var(--wv-accent)',
        color: 'var(--wv-card)',
        border: 'none',
        borderRadius: 6,
        padding: '5px 10px',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

export function DayCard({
  date, isToday, currentHomeHour, hoveredHour, onHoverHour,
  homeTz, calendars, eventsByCalId, allDayEvents = [], eventsLoading, eventsError,
  onPillHover, onPillClick, hoveredEventNumber, clickedEventNumber,
  isAuthenticated, gcalConnected,
  isSummarySelected, onSelectForSummary,
  onSlotClick, meetingPickHours,
  onOpenCalendarPreferences,
  use24HourFormat = false,
}) {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const dateISO = date.toISODate();
  const handlePillClick = (sequentialNumber) =>
    onPillClick && onPillClick(dateISO, sequentialNumber);
  const dayStartHome = date.setZone(homeTz).startOf('day');
  const sharedSlots = useMemo(() => buildHourList(dayStartHome, homeTz, use24HourFormat), [homeTz, dayStartHome, use24HourFormat]);

  // Single-zone fallback used when the multi-zone agenda endpoint is
  // unavailable (no auth, non-supporter, or network failure).
  const buildLocalMarkdown = useCallback(() => {
    function fmt(hourFloat, use24) {
      const h = Math.floor(hourFloat);
      const m = Math.round((hourFloat - h) * 60);
      if (use24) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const ampm = h >= 12 ? 'pm' : 'am';
      const h12 = ((h + 11) % 12) + 1;
      return `${h12}:${String(m).padStart(2, '0')}${ampm}`;
    }
    const heading = `## ${date.toFormat('cccc, LLL d, yyyy')} (${homeTz})`;
    const lines = [heading, ''];
    const calNameById = Object.fromEntries(calendars.map(c => [c.id, c.name || '']));
    const timed = [];
    Object.entries(eventsByCalId || {}).forEach(([calId, evs]) => {
      (evs || []).forEach(e => timed.push({ ...e, _cal: calNameById[calId] || '' }));
    });
    timed.sort((a, b) => a.startHour - b.startHour);
    for (const e of timed) {
      const start = fmt(e.startHour, use24HourFormat);
      const end = fmt(e.startHour + e.durationHours, use24HourFormat);
      const cal = e._cal ? ` — ${e._cal}` : '';
      lines.push(`- ${start}–${end} ${e.title}${cal}`);
    }
    for (const e of (allDayEvents || [])) {
      const cal = e.calendarName ? ` — ${e.calendarName}` : '';
      lines.push(`- All day: ${e.title}${cal}`);
    }
    return lines.join('\n');
  }, [date, homeTz, calendars, eventsByCalId, allDayEvents, use24HourFormat]);

  // Fetch the same multi-zone markdown the CLI prints. Falls back to a
  // single-zone local format on any failure.
  const onCopyMarkdown = useCallback(async () => {
    try {
      const jwt = await getToken();
      if (!jwt) throw new Error('not signed in');
      const res = await fetch(`/api/agent/agenda?days=1&start=${dateISO}&format=markdown`, {
        headers: { Authorization: `Bearer ${jwt}`, Accept: 'text/markdown' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const md = await res.text();
      writeTextToClipboard(md);
      showToast('Copied to clipboard');
    } catch {
      writeTextToClipboard(buildLocalMarkdown());
      showToast('Copied (single-zone fallback)');
    }
  }, [getToken, dateISO, buildLocalMarkdown]);

  const showSignIn = !isAuthenticated;
  // Only show "Connect Google Calendar" when we're sure: gcal status said
  // "not connected" AND we have no enabled calendars to render. The status
  // endpoint is racy with calendar list / events fetching — if calendars
  // already loaded, the user IS connected regardless of status state.
  const showConnectGcal = isAuthenticated && gcalConnected === false && calendars.length === 0;
  const showEmpty = !showSignIn && !showConnectGcal && !eventsLoading && !eventsError &&
    calendars.length > 0 && allDayEvents.length === 0 &&
    Object.values(eventsByCalId).every(list => !list || list.length === 0);

  return (
    <article className={`wv2-day-card${isToday ? ' wv2-day-card--today' : ''}${isSummarySelected ? ' wv2-day-card--summary-pick' : ''}`}>
      <header
        className="wv2-day-header"
        onClick={onSelectForSummary ? () => onSelectForSummary(dateISO) : undefined}
        style={onSelectForSummary ? { cursor: 'pointer' } : undefined}
        title={onSelectForSummary ? t('dayCard.summaryHint') : undefined}
      >
        <span className="wv2-day-header-weekday">{date.toFormat('cccc')}</span>
        <span className="wv2-day-header-date">{date.toFormat('LLL d')}</span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onCopyMarkdown(); }}
          title="Copy day as markdown"
          aria-label="Copy day as markdown"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 4,
            cursor: 'pointer',
            color: 'var(--wv-text-muted)',
            display: 'inline-flex',
            alignItems: 'center',
            marginLeft: 'auto',
          }}
        >
          <Copy size={14} />
        </button>
      </header>
      <div
        className="wv2-day-body"
        style={hoveredHour != null ? { '--wv-hover-frac': `${(hoveredHour + 0.5) / 24}` } : undefined}
      >
        {hoveredHour != null && <div className="wv2-hover-line" aria-hidden="true" />}

        {showSignIn && (
          <a href="/sign-in" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
            <V2Notice>{t('dayCard.signInToLoad')}</V2Notice>
          </a>
        )}
        {showConnectGcal && (
          <V2Notice
            action={onOpenCalendarPreferences && (
              <AddCalendarButton onClick={onOpenCalendarPreferences} label={t('dayCard.addCalendar')} />
            )}
          >
            {t('dayCard.connectGcalHint')}
          </V2Notice>
        )}
        {!showSignIn && !showConnectGcal && eventsLoading && (
          <V2Notice><Loader2 size={14} className="wv-spin" /> {t('dayCard.loadingEvents')}</V2Notice>
        )}
        {!showSignIn && !showConnectGcal && eventsError && !eventsLoading && (
          <V2Notice>{t('dayCard.errorLoadingEvents', { error: String(eventsError) })}</V2Notice>
        )}
        {!showSignIn && !showConnectGcal && !eventsLoading && calendars.length === 0 && (
          <V2Notice
            action={onOpenCalendarPreferences && (
              <AddCalendarButton onClick={onOpenCalendarPreferences} label={t('dayCard.addCalendar')} />
            )}
          >
            {t('dayCard.noCalendarsEnabled')}
          </V2Notice>
        )}
        {showEmpty && <V2Notice>{t('dayCard.noEvents')}</V2Notice>}

        {!showSignIn && !showConnectGcal && allDayEvents.length > 0 && (
          <div className="wv2-allday-strip">
            <span className="wv2-allday-strip-label">{t('dailySummary.allDay')}</span>
            <div className="wv2-allday-strip-events">
              {allDayEvents.map(ev => (
                <span
                  key={ev.id}
                  className={`wv2-allday-pill wv2-allday-pill--${ev.status || 'accepted'}`}
                  style={{ '--wv-event-color': ev.calendarColor || 'var(--wv-accent)' }}
                  title={ev.calendarName ? `${ev.title} · ${ev.calendarName}` : ev.title}
                >
                  {ev.title}
                </span>
              ))}
            </div>
          </div>
        )}

        {!showSignIn && !showConnectGcal && calendars.map(cal => (
          <CalRow
            key={cal.key}
            calendar={cal}
            events={eventsByCalId[cal.id] || []}
            slots={sharedSlots}
            hoveredHour={hoveredHour}
            onHoverHour={onHoverHour}
            nowHour={isToday ? currentHomeHour : null}
            onPillHover={onPillHover}
            onPillClick={handlePillClick}
            onSlotClick={onSlotClick}
            meetingPickHours={meetingPickHours}
            hoveredEventNumber={isSummarySelected ? hoveredEventNumber : null}
            clickedEventNumber={isSummarySelected ? clickedEventNumber : null}
          />
        ))}
      </div>
    </article>
  );
}
